/**
 * These styles are technically not part of the basket but are closely related. So keeping it in the basket folder for now.
 */
import styled, { css } from "styled-components";
import { getBreakpoint } from "../../../../themes/common";
import { type ZIndexType, theme } from "../theme/toyotaTheme";
import * as Button from "./Button";
import * as NavigationStepStyles from "./NavigationStep";

export const NAV_HEIGHT = 62;
export const NAV_HEIGHT_MOBILE = 66;

type StickyWrapperProps = {
    position?: "fixed" | "relative";
    top?: number;
    zIndexName?: NonNullable<ZIndexType>;
};

export const StickyWrapper = styled.div<StickyWrapperProps>`
    position: fixed;
    top: var(--visibleHeaderHeight);
    left: 0;
    right: 0;
    width: 100%;
    height: ${NAV_HEIGHT}px;
    background-color: ${theme.colors.primaryBlack};
    transition: top 0.5s ease-out; /* Transition of top should match Zaidans implementation */
    z-index: ${theme.zIndex.navBar};

    ${({ position }) =>
        position &&
        css`
            position: ${position};
        `};

    ${({ top }) =>
        top &&
        css`
            top: calc(var(--visibleHeaderHeight) + ${top}px);
        `};

    @media ${getBreakpoint("down", "lg")} {
        height: ${NAV_HEIGHT_MOBILE}px;
    }

    /* Follows Zaidan breakpoints */
    @media (min-width: 768px) and (max-width: 991px) {
        z-index: ${theme.zIndex.materialbox};
    }

    /* Override the zindex when the mobile basket is open */
    ${({ zIndexName }) =>
        zIndexName &&
        css`
            z-index: ${theme.zIndex[zIndexName]};
        `};
`;

export const DesktopNav = styled.div`
    display: none;

    @media ${getBreakpoint("up", "lg")} {
        display: block;
    }

    ${NavigationStepStyles.Wrapper} {
        flex-direction: row;
        justify-content: center;
        margin: 0 auto;
        padding: ${theme.space[2]}px 0;

        /* First item links shouldn't have padding as this cause the items to look off-center */
        a:not(:first-child) {
            padding-left: 45px;
        }
    }

    ${NavigationStepStyles.Step} {
        display: flex;
        flex-direction: row;
        align-items: center;
        color: ${theme.colors.grey3};
    }

    html[dir="rtl"] & {
        ${NavigationStepStyles.Wrapper} {
            a:not(:first-child) {
                padding-left: 0;
                padding-right: 45px;
            }
        }
    }
`;

export const MobileNav = styled.div`
    @media ${getBreakpoint("up", "lg")} {
        display: none;
    }

    ${NavigationStepStyles.Step} {
        display: block;
        justify-content: center;
        padding: 8px 0 12px;
        font-size: 1.6rem;
        line-height: 2.3rem;
    }

    ${NavigationStepStyles.Wrapper} {
        flex-direction: column;
        padding: 16px 0 22px;
        ${NavigationStepStyles.Step} {
            padding: 8px 0;
        }
    }
`;

export const MobileNavHeader = styled.div<{ disabled?: boolean }>`
    position: relative; /* necessary for FloatingNav */
    transition: opacity 0.3s;
    opacity: ${({ disabled }) => (disabled ? 0.75 : 1)};
`;

export const MobileNavButton = styled(Button.Link)<{ hasBasketButton?: boolean }>`
    width: 100%;
    padding: 0; /* Needed on model / engine and necessary for FloatingNav */
    padding-left: 10px;
    padding-right: 20px;
    cursor: ${({ disabled }) => (disabled ? "normal" : "pointer")};

    html[dir="rtl"] & {
        padding-left: 20px;
        padding-right: 10px;
    }
`;

export const BackButton = styled(Button.Link)`
    display: flex;
    position: absolute;
    left: 0;
    align-items: center;
    line-height: 1.8rem;
    svg {
        color: ${theme.colors.primaryWhite};
    }
    span {
        display: none;
        margin-left: 10px;
        font-size: 1.3rem;
        color: ${theme.colors.primaryWhite};
        @media ${getBreakpoint("up", "xl")} {
            display: inline-block;
        }
    }

    html[dir="rtl"] & {
        left: auto;
        right: 0;
        span {
            margin-left: 0;
            margin-right: 10px;
        }
    }
`;

export const Badge = styled.div`
    margin: 0 8px;
    padding: 5px 12px;
    font-family: ${theme.fonts.bold};
    font-size: 1.4rem;
    color: ${theme.colors.primaryWhite};
    background-color: ${theme.colors.primaryBlack};
    border-radius: 20px;
`;

export const StepIndicator = styled.span`
    display: block;
    margin: 0 8px;
    font-family: ${theme.fonts.regular};
    font-size: 1.3rem;
    color: ${theme.colors.grey3};
`;

export const StepText = styled.span`
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
`;

export const NavigationDropdown = styled.div`
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    padding: 0 ${theme.space[3]}px;
    background-color: ${theme.colors.primaryBlack};
    border-top: 1px solid ${theme.colors.grey5};
    border-radius: 0px 0px 4px 4px;
    box-shadow: 0px 10px 20px rgba(0, 0, 0, 0.08);
`;
